import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { type KeystoneOfferEligibility } from "data/keystone-api-mapping";
import { useKeystone, useSelectedPolicy } from "data/ss-data-hooks";
import { UnauthorizedRequestError, isError } from "data/ss-error";
import { handleUnauthorized, logError } from "utils";

// path looks like "/my/property-services/:offerInternalName/:offerStep"
const OFFER_INTERNAL_NAME_PATH_INDEX = 3;

export const useEligibility = () => {
  const keystone = useKeystone();
  const navigate = useNavigate();
  const { data } = useSelectedPolicy();
  const [eligibilityData, setEligibilityData] =
    useState<KeystoneOfferEligibility>();
  const [loading, setLoading] = useState(!data?.keystonePolicyId);

  const { pathname, state: locationState } = useLocation();
  const offerInternalName = pathname.split("/")[OFFER_INTERNAL_NAME_PATH_INDEX];

  useEffect(() => {
    if (!data?.keystonePolicyId) {
      return;
    }

    const handleRedirects = (data: KeystoneOfferEligibility) => {
      if (!data.isEligible) {
        // EXPIRED?
        navigate("/my/property-services/ineligible", { replace: true });
      } else if (data.offerStatusCode === "ENROLLED") {
        navigate(
          `/my/property-services/${data.offeringInternalName}/registered`,
          {
            replace: true,
          }
        );
      } else {
        return;
      }
    };

    const handleNotOk = async (res, endpoint) => {
      if (res instanceof UnauthorizedRequestError) {
        handleUnauthorized({ endpoint });
      } else {
        logError(`Creating Offer: ${res.status} ${res.statusText}`);
        navigate("/my/overview");
      }
    };

    // Fetch the eligibility data if needed
    if (
      offerInternalName === (eligibilityData?.offeringInternalName ?? "N/A") &&
      eligibilityData?.insightPolicyId === data.keystonePolicyId
    ) {
      handleRedirects(eligibilityData);
    } else {
      setLoading(true);
      (async () => {
        const offerRes = await keystone.createOffer(
          data.keystonePolicyId,
          offerInternalName
        );
        if (!isError(offerRes)) {
          handleRedirects(offerRes);
          setEligibilityData(offerRes);
        } else {
          await handleNotOk(
            offerRes,
            `offer/${data.keystonePolicyId}/offering/${offerInternalName}`
          );
          return;
        }
        setLoading(false);
      })();
    }
  }, [
    keystone,
    data?.keystonePolicyId,
    navigate,
    pathname,
    locationState,
    offerInternalName,
    eligibilityData,
  ]);

  return { eligibilityData, loading };
};
